import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import { categories } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mulebuyspreadsheet.lifestyle';
const canonicalUrl = `${siteUrl}/shopping-guide`;

const shoppingGuideJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Shopping Guide – Mulebuy Spreadsheet Store Navigation',
  description: 'Learn how to navigate the Mulebuy Spreadsheet store, find exactly what you need across 14 product categories, and complete your purchase with confidence on kaspreadsheet.com.',
  url: canonicalUrl,
  about: {
    '@type': 'Organization',
    name: 'Mulebuy Spreadsheet Hub',
    url: siteUrl,
  },
};

const heroBg = 'https://readdy.ai/api/search-image?query=Warm%20inviting%20retail%20space%20with%20organized%20product%20displays%20on%20wooden%20shelves%2C%20natural%20sunlight%20streaming%20through%20large%20windows%2C%20a%20person%20browsing%20items%20with%20focused%20attention%2C%20lifestyle%20shopping%20atmosphere%20with%20cream%20and%20sage%20tones%2C%20editorial%20retail%20photography%20with%20aspirational%20and%20welcoming%20mood%2C%20soft%20shadows%20and%20organic%20composition&width=1600&height=700&seq=shopping-guide-hero&orientation=landscape';

const stepOneImg = 'https://readdy.ai/api/search-image?query=Overhead%20shot%20of%20hands%20hovering%20over%20a%20laptop%20keyboard%20with%20a%20dropdown%20menu%20visible%20on%20screen%2C%20warm%20desk%20lamp%20lighting%2C%20organized%20workspace%20with%20a%20notebook%20and%20coffee%20cup%20nearby%2C%20focused%20and%20intentional%20browsing%20atmosphere%20with%20terracotta%20and%20cream%20tones%2C%20digital%20navigation%20tutorial%20photography%20with%20clean%20minimalist%20aesthetic&width=800&height=500&seq=shopping-step1&orientation=landscape';

const stepTwoImg = 'https://readdy.ai/api/search-image?query=Modern%20ecommerce%20product%20grid%20displayed%20on%20a%20large%20monitor%20screen%20with%20colorful%20category%20cards%20and%20thumbnails%2C%20warm%20ambient%20home%20office%20lighting%2C%20a%20hand%20holding%20a%20smartphone%20beside%20the%20monitor%20showing%20the%20same%20page%2C%20seamless%20cross-device%20shopping%20concept%20with%20sage%20and%20cream%20tones%2C%20digital%20shopping%20experience%20photography%20with%20bright%20and%20inviting%20mood&width=800&height=500&seq=shopping-step2&orientation=landscape';

const stepThreeImg = 'https://readdy.ai/api/search-image?query=Secure%20online%20checkout%20concept%20with%20a%20laptop%20screen%20showing%20a%20confirmation%20page%20with%20a%20green%20checkmark%2C%20a%20credit%20card%20placed%20neatly%20beside%20the%20laptop%20on%20a%20clean%20desk%2C%20warm%20natural%20lighting%2C%20trust%20and%20satisfaction%20atmosphere%20with%20amber%20and%20cream%20tones%2C%20successful%20purchase%20completion%20photography%20with%20professional%20and%20reassuring%20mood&width=800&height=500&seq=shopping-step3&orientation=landscape';

export default function ShoppingGuidePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <SEO
        title="Shopping Guide – How to Navigate Mulebuy Spreadsheet Store"
        description="Learn how to navigate the Mulebuy Spreadsheet store, find exactly what you need across 14 product categories including Shoes, Hoodies, T-Shirts, Jackets, and more, and complete your purchase with confidence on kaspreadsheet.com."
        keywords="mulebuy spreadsheet shopping guide, how to shop mulebuy spreadsheet, mulebuy store navigation, kaspreadsheet shopping"
        canonical={canonicalUrl}
        ogType="website"
        jsonLd={shoppingGuideJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full h-[420px] md:h-[520px] overflow-hidden">
        <img
          src={heroBg}
          alt="Shopping Guide - Mulebuy Spreadsheet Store Navigation"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4 max-w-3xl">
            Your Complete Shopping Guide
          </h1>
          <p className="text-white/80 text-sm md:text-lg max-w-xl leading-relaxed">
            Learn how to navigate the Mulebuy Spreadsheet store, find exactly what you need across 14 product categories, and complete your purchase with confidence on kaspreadsheet.com.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="w-full max-w-4xl mx-auto px-6 py-14 md:py-20 text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
          How to Shop Through Mulebuy Spreadsheet
        </h2>
        <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
          This platform is your gateway to the full Mulebuy Spreadsheet store. Every category link on this site connects you directly to the corresponding section on kaspreadsheet.com, where you can browse detailed product listings, compare items, and complete secure purchases. Follow the three simple steps below to get started.
        </p>
      </section>

      {/* Step-by-step */}
      <section className="w-full max-w-5xl mx-auto px-6 pb-14 md:pb-20">
        <div className="space-y-16">
          {/* Step 1 */}
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className="w-full md:w-1/2">
              <img
                src={stepOneImg}
                alt="Step 1 - Choose a category from the dropdown"
                className="w-full h-[280px] md:h-[320px] object-cover rounded-lg"
              />
            </div>
            <div className="w-full md:w-1/2">
              <span className="inline-block px-3 py-1 text-xs font-semibold bg-accent-100 text-accent-900 rounded-full mb-3">
                Step 1
              </span>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                Choose Your Product Category
              </h3>
              <p className="text-foreground-600 text-sm leading-relaxed">
                Start by selecting a category from the navigation dropdown at the top of this page. We have organized 14 product categories including Shoes, Hoodies, T-Shirts, Jackets, Pants, Headwear, Sets, Underwear, Jerseys, Accessories, Bags, Belts, Perfumes, and Other Stuff. Each category link takes you directly to the curated product collection on kaspreadsheet.com.
              </p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex flex-col md:flex-row-reverse items-center gap-8 md:gap-12">
            <div className="w-full md:w-1/2">
              <img
                src={stepTwoImg}
                alt="Step 2 - Browse products and compare"
                className="w-full h-[280px] md:h-[320px] object-cover rounded-lg"
              />
            </div>
            <div className="w-full md:w-1/2">
              <span className="inline-block px-3 py-1 text-xs font-semibold bg-accent-100 text-accent-900 rounded-full mb-3">
                Step 2
              </span>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                Browse and Compare Products
              </h3>
              <p className="text-foreground-600 text-sm leading-relaxed">
                Once on the category page, browse through the product listings at your own pace. Each product card shows key details like images, pricing, and availability. Use the built-in comparison tools and spreadsheet resources to evaluate options side by side. Our Mulebuy Spreadsheet comparison system helps you make informed purchasing decisions based on real data.
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className="w-full md:w-1/2">
              <img
                src={stepThreeImg}
                alt="Step 3 - Complete your purchase securely"
                className="w-full h-[280px] md:h-[320px] object-cover rounded-lg"
              />
            </div>
            <div className="w-full md:w-1/2">
              <span className="inline-block px-3 py-1 text-xs font-semibold bg-accent-100 text-accent-900 rounded-full mb-3">
                Step 3
              </span>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                Complete Your Purchase
              </h3>
              <p className="text-foreground-600 text-sm leading-relaxed">
                When you have found the items you want, complete your order through the secure checkout on kaspreadsheet.com. The platform supports multiple payment methods and provides order tracking so you always know where your purchase stands. All transactions are processed safely on the main store.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* All Categories Quick Links */}
      <section className="w-full bg-background-100 py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-3">
            All Product Categories
          </h2>
          <p className="text-foreground-600 text-sm text-center mb-10 max-w-xl mx-auto">
            Click any category below to go directly to its product listing on kaspreadsheet.com
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2.5 px-4 py-3 bg-background-50 rounded-lg border border-background-200 hover:border-accent-300 hover:bg-accent-50/30 transition-all cursor-pointer group"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 group-hover:bg-accent-200 transition-colors flex-shrink-0">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
                <span className="text-sm font-medium text-foreground-700 group-hover:text-foreground-950 transition-colors whitespace-nowrap">
                  {cat.name}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="w-full max-w-4xl mx-auto px-6 py-14 md:py-20">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10">
          Shopping Tips for the Best Experience
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-6 bg-background-50 rounded-lg border border-background-200">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 mb-4">
              <i className="ri-price-tag-3-line text-lg"></i>
            </div>
            <h4 className="font-heading text-lg font-bold text-foreground-950 mb-2">Check for Deals</h4>
            <p className="text-foreground-600 text-sm leading-relaxed">
              New products and special offers are added regularly. Visit often and bookmark your favorite categories so you never miss a great deal on the items you want.
            </p>
          </div>
          <div className="p-6 bg-background-50 rounded-lg border border-background-200">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-4">
              <i className="ri-file-list-3-line text-lg"></i>
            </div>
            <h4 className="font-heading text-lg font-bold text-foreground-950 mb-2">Use Spreadsheets for Comparison</h4>
            <p className="text-foreground-600 text-sm leading-relaxed">
              Our Mulebuy Spreadsheet tools help you compare products across categories, track pricing history, and make data-driven purchasing decisions. Check the comparison page for detailed guidance.
            </p>
          </div>
          <div className="p-6 bg-background-50 rounded-lg border border-background-200">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 mb-4">
              <i className="ri-shield-check-line text-lg"></i>
            </div>
            <h4 className="font-heading text-lg font-bold text-foreground-950 mb-2">Shop Safely</h4>
            <p className="text-foreground-600 text-sm leading-relaxed">
              All purchases are completed on kaspreadsheet.com, a trusted platform with secure payment processing. Always verify you are on the official site before entering payment details.
            </p>
          </div>
          <div className="p-6 bg-background-50 rounded-lg border border-background-200">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-200 text-foreground-700 mb-4">
              <i className="ri-customer-service-2-line text-lg"></i>
            </div>
            <h4 className="font-heading text-lg font-bold text-foreground-950 mb-2">Need Help?</h4>
            <p className="text-foreground-600 text-sm leading-relaxed">
              If you have questions about specific products or orders, reach out through the support channels on kaspreadsheet.com. Their customer service team is available to help with any issues.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full bg-background-100 py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            Ready to Start Shopping?
          </h2>
          <p className="text-foreground-600 text-sm md:text-base mb-8 max-w-lg mx-auto">
            Browse all 14 product categories on the full Mulebuy Spreadsheet store. Thousands of products, competitive prices, and secure checkout await.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center px-6 py-3 text-sm font-semibold bg-secondary-500 text-white rounded-full hover:bg-secondary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line mr-2"></i>
              Back to Home
            </Link>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-6 py-3 text-sm font-semibold bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-shopping-bag-3-line mr-2"></i>
              Visit Store Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}